import toast from 'react-hot-toast';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';

import Honeybadger from '@honeybadger-io/js';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { requestApi } from 'api/Api';
import { deleteMember } from 'api/Member';

import StaffMember from 'components/Staff/components/StaffMember';
import SupervisorSettings from 'components/Staff/components/SupervisorSettings';
import { showAlert } from 'components/shared/Alert/Alert';
import Confirm from 'components/shared/Modal/Confirm/Confirm';

const PracticeStaffModals = ({
  show,
  setShow,
  selectedStaff,
  setSelectedStaff,
  memberToSupervisor,
  deletePracticeStaffModal,
  setDeletePracticeStaffModal
}) => {
  const { practice } = useOutletContext();
  const { id } = useParams();

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const impersonateUser = async () => {
    try {
      sessionStorage.removeItem('myriad-session-reference-id');
      const res = await requestApi({
        url: '/api/admin/practice/impersonate',
        params: {
          targetId: selectedStaff.id
        },
        navigate
      });

      const { code, redirect, error } = res;
      switch (code) {
        case -1:
          navigate(redirect);
          break;
        case 0:
          setShow({ ...show, confirmImpersonate: false });
          window.open(redirect, '_blank');
          break;
        default:
          Honeybadger.notify(
            `file: staff/PractitionersBasicInfo, method: impersonateUser - try, error: ${
              error ?? 'Theres been an error'
            }`
          );
          if (error) toast.error(error);
          break;
      }
    } catch (error) {
      Honeybadger.notify(
        `file: staff/PractitionersBasicInfo, method: impersonateUser - try, error: ${
          error ?? 'Theres been an error'
        }`
      );
    }
  };
  const mutateDelete = useMutation({
    mutationFn: () => deleteMember(navigate, { memberId: selectedStaff.id, practiceId: id }),
    onSuccess: (data) => {
      switch (data?.code) {
        case 0:
          queryClient.invalidateQueries(['staffPracticeAdmin']);
          setDeletePracticeStaffModal(null);
          showAlert({ title: 'Member deleted successfully', color: 'success' });
          break;
        default:
          showAlert({ title: 'Failed to delete practice member.', color: 'danger' });
          break;
      }
    }
  });
  return (
    <>
      <Confirm
        variant="primary"
        primaryBtnTxt="Confirm"
        title="Achtung!"
        icon="eye-mono"
        message="You're about to impersonate a provider. Your permissions are limited per HIPAA compliance and activity is logged."
        handleContinue={() => impersonateUser()}
        handleOpen={show?.confirmImpersonate}
        handleClose={() => setShow({ ...show, confirmImpersonate: false })}
      />
      {!!show.staffEdit && (
        <StaffMember
          practiceId={practice.id}
          member={{ ...selectedStaff, user_id: selectedStaff.id }}
          handleOpen={!!show.staffEdit}
          onClose={() => setShow({ ...show, staffEdit: false })}
          setSelectedStaff={setSelectedStaff}
        />
      )}
      {!!show.supervisorSettings && (
        <SupervisorSettings
          member={{
            ...memberToSupervisor,
            practice_id: id,
            user_id: memberToSupervisor.id
          }}
          showModal={!!show.supervisorSettings}
          hideModal={() => setShow({ ...show, supervisorSettings: false })}
        />
      )}
      {!!deletePracticeStaffModal && (
        <Confirm
          variant="danger"
          primaryBtnTxt="Delete"
          title="Delete member"
          icon="trash-mono"
          message="Are you sure you want to delete this member?"
          handleContinue={() => {
            mutateDelete.mutate();
            setDeletePracticeStaffModal(false);
          }}
          handleOpen={!!deletePracticeStaffModal}
          handleClose={() => setDeletePracticeStaffModal(false)}
        />
      )}
    </>
  );
};

export default PracticeStaffModals;
